import { useState, useEffect } from "react";
import "./EnvHealth.css";

const STATUS = { OK: "ok", WARN: "warn", ERROR: "error" };

const STATUS_LABEL_CLASS = {
  [STATUS.OK]: "env-health-summary-label env-health-summary-label--ok",
  [STATUS.WARN]: "env-health-summary-label env-health-summary-label--warn",
  [STATUS.ERROR]: "env-health-summary-label env-health-summary-label--error",
};

const STATUS_LABEL = {
  [STATUS.OK]: "All systems OK",
  [STATUS.WARN]: "Some warnings",
  [STATUS.ERROR]: "Issues detected",
};

function buttonModifier(status, isLoading, data) {
  if (isLoading || !data) return "env-health-button--loading";
  switch (status) {
    case STATUS.OK:
      return "env-health-button--green";
    case STATUS.WARN:
      return "env-health-button--amber";
    default:
      return "env-health-button--red";
  }
}

function overallStatus(data) {
  if (!data) return null;

  const allServicesOk = data.services.every((s) => s.running);
  const hostsOk = data.hostnames.every((h) => h.configured);
  const raterGemsOk = data.rater_gems.every((s) => !s.error);

  if (allServicesOk && hostsOk && raterGemsOk) return STATUS.OK;

  const criticalDown = data.services.filter((s) => !s.running);
  if (criticalDown.length > 0 || !hostsOk) return STATUS.ERROR;

  return STATUS.WARN;
}

const STATUS_SYMBOL = {
  [STATUS.OK]: "✓",
  [STATUS.WARN]: "?",
  [STATUS.ERROR]: "✕",
};

function Indicator({ status }) {
  return (
    <span className={`env-health-indicator env-health-indicator--${status}`}>
      {STATUS_SYMBOL[status]}
    </span>
  );
}

function ServicesSection({ services, title, docker }) {
  const filtered = services.filter((s) => s.docker === docker);
  if (filtered.length === 0) return null;

  return (
    <div className="env-health-section">
      <div className="env-health-section-title">{title}</div>
      {filtered.map((s) => (
        <div key={s.name} className="env-health-row">
          <Indicator status={s.running ? STATUS.OK : STATUS.ERROR} />
          <span className="env-health-name">
            {s.name}
          </span>
          <span
            className={`env-health-detail ${
              s.running ? "env-health-detail--ok" : "env-health-detail--error"
            }`}
          >
            {s.running ? "running" : "not running"}
          </span>
          <span className="env-health-url">{URL.parse(s.url).host}</span>
        </div>
      ))}
    </div>
  );
}

function RepositoriesSection({ repositories }) {
  return (
    <div className="env-health-section">
      <div className="env-health-section-title">Repositories</div>
      {repositories.map((r) => {
        if (!r.found) {
          return (
            <div key={r.name} className="env-health-row">
              <Indicator status={STATUS.WARN} />
              <span className="env-health-name">{r.name}</span>
              <span className="env-health-detail">not found</span>
            </div>
          );
        }
        const isAhead = r.divergence && r.divergence !== "(up to date)";
        return (
          <div key={r.name} className="env-health-row">
            <Indicator status={isAhead ? STATUS.WARN : STATUS.OK} />
            <span className="env-health-name">{r.name}</span>
            <span className="env-health-detail">
              {r.branch} &middot; {r.commit} &middot; {r.updated} ago &middot;{" "}
              {r.divergence}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function HostnamesSection({ hostnames }) {
  return (
    <div className="env-health-section">
      <div className="env-health-section-title">Hostnames</div>
      {hostnames.map((h) => (
        <div key={h.hostname} className="env-health-row">
          <Indicator status={h.configured ? STATUS.OK : STATUS.ERROR} />
          <span className="env-health-name">{h.hostname}</span>
          <span
            className={`env-health-detail ${
              h.configured
                ? "env-health-detail--ok"
                : "env-health-detail--error"
            }`}
          >
            {h.configured ? "configured" : "missing"}
          </span>
        </div>
      ))}
    </div>
  );
}

function RaterGemsSection({ raterGems }) {
  return (
    <div className="env-health-section">
      <div className="env-health-section-title">Rater Gems</div>
      {raterGems.map((service) => {
        if (service.error) {
          return (
            <div key={service.name} className="env-health-row">
              <Indicator status={STATUS.ERROR} />
              <span className="env-health-name">{service.name}</span>
              <span className="env-health-detail env-health-detail--error">
                {service.error}
              </span>
            </div>
          );
        }
        return (
          <div key={service.name}>
            {Object.entries(service.gems).map(([gemName, gem]) => (
              <div key={gemName} className="env-health-row">
                <Indicator status={gem.warning ? STATUS.WARN : STATUS.OK} />
                <span className="env-health-name">
                  {service.name}/{gemName}
                </span>
                <span className="env-health-detail">
                  {gem.version || "not loaded"}{" "}
                  {gem.path ? (
                    <span title={gem.path}>
                      ({gem.path.split("/").slice(-2).join("/")})
                    </span>
                  ) : null}
                  {gem.warning ? (
                    <span className="env-health-summary-label env-health-summary-label--warn"><br />WARNING: {gem.warning}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

const EnvHealth = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const status = overallStatus(data);

  const buttonClass = [
    "env-health-button",
    buttonModifier(status, isLoading, data),
  ].join(" ");

  const fetchEnvHealth = async ({ bustCache = false } = {}) => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const response = await fetch("/dev_tools/env-health", {
        cache: bustCache ? "no-store" : "default",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      setData(json);
    } catch (e) {
      setFetchError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEnvHealth();
  }, []);

  const handleToggle = () => {
    if (!isOpen && !data && !isLoading) {
      fetchEnvHealth();
    }
    setIsOpen((prev) => !prev);
  };

  const handleRefresh = (e) => {
    e.stopPropagation();
    fetchEnvHealth({ bustCache: true });
  };

  return (
    <div id="env-health-container">
      <button className={buttonClass} onClick={handleToggle}>
        <span className="env-health-text">Env health</span>
        <span className="env-health-arrow">{isOpen ? "▼" : "▶"}</span>
      </button>

      {isOpen && (
        <div className="env-health-panel">
          {fetchError && (
            <div className="env-health-error-banner">
              Failed to load: {fetchError}
            </div>
          )}
          {isLoading && <div className="env-health-detail">Loading...</div>}
          {data && !isLoading && (
            <>
              <div className="env-health-summary">
                <span className={STATUS_LABEL_CLASS[status]}>
                  {STATUS_LABEL[status]}
                </span>
                <button
                  className="env-health-refresh-button"
                  onClick={handleRefresh}
                >
                  Refresh
                </button>
              </div>

              <ServicesSection
                services={data.services}
                title="Local Services"
                docker={false}
              />
              <ServicesSection
                services={data.services}
                title="Docker Services"
                docker={true}
              />
              <RepositoriesSection repositories={data.repositories} />
              <HostnamesSection hostnames={data.hostnames} />
              <RaterGemsSection raterGems={data.rater_gems} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EnvHealth;
